import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useServices } from '@/contexts/app-services';
import {
  prepareGoalCreate,
  prepareGoalUpdate,
} from '@/entities/goal/operations';
import type {
  Goal,
  GoalCreate,
  GoalCreateInput,
  GoalUpdateInput,
} from '@/entities/goal/types';
import type { PlannedGoal, PlannedGoalCreate } from '@/entities/planned-goal';
import {
  getPlannedGoalReorder,
  getSortedPlannedGoals,
  preparePlannedGoalCreate,
  preparePlannedGoalCreates,
} from '@/entities/planned-goal/operations';
import {
  computeSortKey,
  computeSortKeys,
  getSortedKeys,
} from '@/entities/planned-goal/sort-key';
import { logError } from '@/services/logger';
import { goalKeys } from './goal';

export const plannedGoalKeys = {
  byPlanId: (planId: string) => ['planned-goals', planId] as const,
};

type GoalSource = 'manual' | 'aiSuggestion';
type GoalCreateTrigger =
  | 'autoEmpty'
  | 'manualInsert'
  | 'manualPaste'
  | 'aiSuggestion'
  | 'unknown';

export function plannedGoalsQueryOptions(
  planId: string,
  api: ReturnType<typeof useServices>['api'],
) {
  return queryOptions({
    queryKey: plannedGoalKeys.byPlanId(planId),
    queryFn: async (): Promise<PlannedGoal[]> => {
      return await api.plannedGoals.readPlannedGoalsByPlanId(planId);
    },
  });
}

function makeOptimisticGoalOnCreate(goalCreate: GoalCreate): Goal {
  const now = new Date();
  return {
    ...goalCreate,
    createdAt: now,
    updatedAt: now,
    parentGoalId: goalCreate.parentGoalId ?? null,
  };
}

function makeOptimisticPlannedGoalsOnCreate(
  previousPlannedGoals: PlannedGoal[],
  plannedGoalCreate: PlannedGoalCreate,
): PlannedGoal[] {
  const now = new Date();
  const optimisticPlannedGoal: PlannedGoal = {
    ...plannedGoalCreate,
    createdAt: now,
    updatedAt: now,
  };

  return getSortedPlannedGoals([
    ...previousPlannedGoals,
    optimisticPlannedGoal,
  ]);
}

function makeOptimisticPlannedGoalsOnDelete(
  previousPlannedGoals: PlannedGoal[],
  deleteSortKey: string,
): PlannedGoal[] {
  return previousPlannedGoals.filter((pg) => pg.sortKey !== deleteSortKey);
}

function getEditedFieldsSinceCreate(
  cachedGoal: Goal | undefined,
  serverGoal: Goal,
): GoalUpdateInput | null {
  const edits: GoalUpdateInput = {};

  if (
    cachedGoal?.title !== undefined &&
    cachedGoal.title !== serverGoal.title
  ) {
    edits.title = cachedGoal.title;
  }
  if (
    cachedGoal?.status !== undefined &&
    cachedGoal.status !== serverGoal.status
  ) {
    edits.status = cachedGoal.status;
  }

  return Object.keys(edits).length > 0 ? edits : null;
}

export function usePlannedGoals(planId: string) {
  const { api } = useServices();

  return useQuery(plannedGoalsQueryOptions(planId, api));
}

export function useCreateGoalAtPosition(options?: {
  onOptimisticGoalCreated?: (goalId: string) => void;
}) {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();

  type ExternalVars = {
    planId: string;
    goalCreateInput: GoalCreateInput;
    insertAtPosition: number;
    period: string;
    startAtLocal: string;
    source: GoalSource;
    trigger?: GoalCreateTrigger;
  };

  type InternalVars = {
    goalCreate: GoalCreate;
    plannedGoalCreate: PlannedGoalCreate;
    insertAtPosition: number;
    period: string;
    startAtLocal: string;
    source: GoalSource;
    trigger: GoalCreateTrigger;
  };

  function toInternalVars(vars: ExternalVars): InternalVars {
    const goalCreate = prepareGoalCreate(vars.goalCreateInput);
    const currentPlannedGoals =
      queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(vars.planId),
      ) ?? [];
    const sortedKeys = getSortedKeys(currentPlannedGoals);
    const sortKey = computeSortKey(sortedKeys, vars.insertAtPosition);

    return {
      goalCreate,
      plannedGoalCreate: preparePlannedGoalCreate({
        planId: vars.planId,
        newGoalId: goalCreate.id,
        sortKey,
      }),
      insertAtPosition: vars.insertAtPosition,
      period: vars.period,
      startAtLocal: vars.startAtLocal,
      source: vars.source,
      trigger: vars.trigger ?? 'unknown',
    };
  }

  const mutation = useMutation({
    mutationFn: async ({ goalCreate, plannedGoalCreate }: InternalVars) => {
      const { id: plannedGoalId, planId, sortKey } = plannedGoalCreate;

      const [serverGoal] = await api.goals.createGoals([goalCreate]);
      if (!serverGoal) {
        throw new Error('Goal creation returned no data');
      }

      await api.plannedGoals.addGoalsToPlan([
        { id: plannedGoalId, goalId: serverGoal.id, planId, sortKey },
      ]);

      return { goal: serverGoal };
    },

    onMutate: async ({ goalCreate, plannedGoalCreate }: InternalVars) => {
      const { planId } = plannedGoalCreate;

      await queryClient.cancelQueries({
        queryKey: plannedGoalKeys.byPlanId(planId),
      });

      const previousPlannedGoals = queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(planId),
      );

      const optimisticGoal = makeOptimisticGoalOnCreate(goalCreate);
      const optimisticPlannedGoals = makeOptimisticPlannedGoalsOnCreate(
        previousPlannedGoals || [],
        plannedGoalCreate,
      );

      queryClient.setQueryData(
        goalKeys.byId(optimisticGoal.id),
        optimisticGoal,
      );

      queryClient.setQueryData(
        plannedGoalKeys.byPlanId(planId),
        optimisticPlannedGoals,
      );

      if (options?.onOptimisticGoalCreated) {
        options.onOptimisticGoalCreated(optimisticGoal.id);
      }

      return { previousPlannedGoals, optimisticGoal, planId };
    },

    onSuccess: async ({ goal }, variables, context) => {
      const goalId = context.optimisticGoal.id;
      const planId = context.planId;

      analytics.trackAddedGoal({
        goalId: goal.id,
        period: variables.period,
        startAtLocal: variables.startAtLocal,
        position: variables.insertAtPosition,
        source: variables.source,
      });

      const cachedGoal = queryClient.getQueryData<Goal>(goalKeys.byId(goalId));
      const editedFields = getEditedFieldsSinceCreate(cachedGoal, goal);

      if (editedFields) {
        const validatedUpdate = prepareGoalUpdate(editedFields);
        await api.goals
          .updateGoal(goalId, validatedUpdate)
          .catch((err: unknown) => {
            logError(
              err instanceof Error ? err : new Error(String(err)),
              `Failed to sync edits made during goal creation ${goalId}`,
            );
          });
      }

      const currentPlannedGoals = queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(planId),
      );

      const isStillInPlan = (currentPlannedGoals ?? []).some(
        (pg) => pg.goalId === goalId,
      );

      if (!isStillInPlan) {
        await api.goals.deleteGoal(goalId);
      }
    },

    onError: (error, variables, context) => {
      logError(error, 'Goal creation failed', {
        planId: variables.plannedGoalCreate.planId,
        insertAtPosition: variables.insertAtPosition,
        trigger: variables.trigger,
      });
      if (context) {
        const goalId = context.optimisticGoal.id;
        const planId = context.planId;

        queryClient.removeQueries({ queryKey: goalKeys.byId(goalId) });
        queryClient.setQueryData(
          plannedGoalKeys.byPlanId(planId),
          context.previousPlannedGoals,
        );

        api.goals.deleteGoal(goalId).catch((err: unknown) => {
          logError(
            err instanceof Error ? err : new Error(String(err)),
            `Failed to clean up orphaned goal ${goalId}`,
          );
        });
      }
    },
  });

  return {
    ...mutation,
    mutate: (vars: ExternalVars) => {
      mutation.mutate(toInternalVars(vars));
    },
    mutateAsync: (vars: ExternalVars) =>
      mutation.mutateAsync(toInternalVars(vars)),
  };
}

export function useCreateGoalsAtPosition(options?: {
  onOptimisticGoalsCreated?: (lastGoalId: string) => void;
}) {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();

  type ExternalVars = {
    planId: string;
    goalTitles: string[];
    parentGoalId?: string;
    insertAtPosition: number;
    period: string;
    startAtLocal: string;
    source: GoalSource;
    trigger?: GoalCreateTrigger;
  };

  type InternalVars = {
    planId: string;
    goalCreates: GoalCreate[];
    plannedGoalCreates: PlannedGoalCreate[];
    insertAtPosition: number;
    period: string;
    startAtLocal: string;
    source: GoalSource;
    trigger: GoalCreateTrigger;
  };

  function toInternalVars(vars: ExternalVars): InternalVars {
    const goalCreates = vars.goalTitles.map((title) =>
      prepareGoalCreate({
        title,
        ...(vars.parentGoalId ? { parentGoalId: vars.parentGoalId } : {}),
      }),
    );
    const currentPlannedGoals =
      queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(vars.planId),
      ) ?? [];
    const sortKeys = computeSortKeys(
      getSortedKeys(currentPlannedGoals),
      vars.insertAtPosition,
      goalCreates.length,
    );

    const plannedGoalCreates = goalCreates.map((gc, i) => {
      const sortKey = sortKeys[i];
      if (sortKey === undefined) {
        throw new Error(
          'computeSortKeys returned fewer keys than goal creates',
        );
      }
      return preparePlannedGoalCreate({
        planId: vars.planId,
        newGoalId: gc.id,
        sortKey,
      });
    });

    return {
      planId: vars.planId,
      goalCreates,
      plannedGoalCreates,
      insertAtPosition: vars.insertAtPosition,
      period: vars.period,
      startAtLocal: vars.startAtLocal,
      source: vars.source,
      trigger: vars.trigger ?? 'unknown',
    };
  }

  const mutation = useMutation({
    mutationFn: async ({
      planId,
      goalCreates,
      plannedGoalCreates,
    }: InternalVars) => {
      const serverGoals = await api.goals.createGoals(goalCreates);
      if (serverGoals.length !== goalCreates.length) {
        throw new Error('Batch goal creation returned mismatched data');
      }

      await api.plannedGoals.addGoalsToPlan(
        plannedGoalCreates.map((pgc, i) => {
          const sg = serverGoals[i];
          if (!sg) {
            throw new Error('Mismatched goal create response');
          }
          return { id: pgc.id, goalId: sg.id, planId, sortKey: pgc.sortKey };
        }),
      );

      return { goals: serverGoals };
    },

    onMutate: async ({
      planId,
      goalCreates,
      plannedGoalCreates,
    }: InternalVars) => {
      await queryClient.cancelQueries({
        queryKey: plannedGoalKeys.byPlanId(planId),
      });

      const previousPlannedGoals = queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(planId),
      );

      const optimisticGoals = goalCreates.map((gc) =>
        makeOptimisticGoalOnCreate(gc),
      );
      for (const og of optimisticGoals) {
        queryClient.setQueryData(goalKeys.byId(og.id), og);
      }

      let optimisticPlannedGoals = previousPlannedGoals ?? [];
      for (const pgc of plannedGoalCreates) {
        optimisticPlannedGoals = makeOptimisticPlannedGoalsOnCreate(
          optimisticPlannedGoals,
          pgc,
        );
      }
      queryClient.setQueryData(
        plannedGoalKeys.byPlanId(planId),
        optimisticPlannedGoals,
      );

      const lastGoalId = optimisticGoals[optimisticGoals.length - 1]?.id;
      if (lastGoalId && options?.onOptimisticGoalsCreated) {
        options.onOptimisticGoalsCreated(lastGoalId);
      }

      return { previousPlannedGoals, optimisticGoals, planId };
    },

    onSuccess: (_data, variables, context) => {
      const { goals: serverGoals } = _data;
      for (let i = 0; i < serverGoals.length; i++) {
        const goal = serverGoals[i];
        if (!goal) {
          continue;
        }
        analytics.trackAddedGoal({
          goalId: goal.id,
          period: variables.period,
          startAtLocal: variables.startAtLocal,
          position: variables.insertAtPosition + i,
          source: variables.source,
        });
      }

      const currentPlannedGoals = queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(context.planId),
      );
      for (const og of context.optimisticGoals) {
        const isStillInPlan = (currentPlannedGoals ?? []).some(
          (pg) => pg.goalId === og.id,
        );
        if (!isStillInPlan) {
          void api.goals.deleteGoal(og.id);
        }
      }
    },

    onError: (error, variables, context) => {
      logError(error, 'Batch goal creation failed', {
        planId: variables.planId,
        insertAtPosition: variables.insertAtPosition,
        trigger: variables.trigger,
      });
      if (context) {
        const { planId } = context;
        queryClient.setQueryData(
          plannedGoalKeys.byPlanId(planId),
          context.previousPlannedGoals,
        );
        for (const og of context.optimisticGoals) {
          queryClient.removeQueries({ queryKey: goalKeys.byId(og.id) });
          api.goals.deleteGoal(og.id).catch((err: unknown) => {
            logError(
              err instanceof Error ? err : new Error(String(err)),
              `Failed to clean up orphaned goal ${og.id}`,
            );
          });
        }
      }
    },
  });

  return {
    ...mutation,
    mutate: (vars: ExternalVars) => {
      mutation.mutate(toInternalVars(vars));
    },
  };
}

interface PendingReorder {
  goalId: string;
  plannedGoalId: string;
  newSortKey: string;
  fromPosition: number;
  toPosition: number;
}

const REORDER_DEBOUNCE_MS = 400;

export function useReorderGoal(
  planId: string,
  period: string,
  startAtLocal: string,
) {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();
  const pendingRef = useRef<PendingReorder | null>(null);

  function applyOptimisticReorder(
    goalId: string,
    newIndex: number,
  ): PendingReorder | null {
    const currentPlannedGoals =
      queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(planId),
      ) ?? [];
    const reorder = getPlannedGoalReorder(
      currentPlannedGoals,
      goalId,
      newIndex,
    );
    if (!reorder) {
      return null;
    }

    queryClient.setQueryData(
      plannedGoalKeys.byPlanId(planId),
      reorder.updatedPlannedGoals,
    );

    return { goalId, ...reorder };
  }

  async function persistReorder(
    pending: PendingReorder,
    snapshotPlanId: string,
    snapshotPeriod: string,
    snapshotStartAtLocal: string,
  ): Promise<void> {
    try {
      await api.plannedGoals.reorderGoalInPlan(
        pending.plannedGoalId,
        pending.newSortKey,
      );
      analytics.trackReorderedGoal({
        goalId: pending.goalId,
        fromPosition: pending.fromPosition,
        toPosition: pending.toPosition,
        period: snapshotPeriod,
        startAtLocal: snapshotStartAtLocal,
      });
    } catch (err) {
      logError(
        err instanceof Error ? err : new Error(String(err)),
        'Goal reorder failed',
      );
      void queryClient.invalidateQueries({
        queryKey: plannedGoalKeys.byPlanId(snapshotPlanId),
      });
    }
  }

  const debouncedPersist = useDebouncedCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }

    const currentPlannedGoals =
      queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(planId),
      ) ?? [];
    const plannedGoalExists = currentPlannedGoals.some(
      (pg) => pg.id === pending.plannedGoalId,
    );

    if (!plannedGoalExists) {
      pendingRef.current = null;
      return;
    }

    pendingRef.current = null;
    await persistReorder(pending, planId, period, startAtLocal);
  }, REORDER_DEBOUNCE_MS);

  function reorderGoal(goalId: string, newIndex: number): void {
    // A drag drop supersedes any pending keyboard reorder. If the pending is for
    // the same goal, discard it to avoid a stale write racing the drag persist.
    // If it's for a different goal, flush it immediately before proceeding.
    if (pendingRef.current !== null) {
      debouncedPersist.cancel();
      const previousPending = pendingRef.current;
      pendingRef.current = null;
      if (previousPending.goalId !== goalId) {
        void persistReorder(previousPending, planId, period, startAtLocal);
      }
    }

    const pending = applyOptimisticReorder(goalId, newIndex);
    if (!pending) {
      return;
    }
    void persistReorder(pending, planId, period, startAtLocal);
  }

  function debouncedReorderGoal(goalId: string, newIndex: number): void {
    // If the user switches to reordering a different goal, flush the pending
    // reorder for the previous goal immediately before starting a new debounce.
    if (pendingRef.current !== null && pendingRef.current.goalId !== goalId) {
      debouncedPersist.cancel();
      const previousPending = pendingRef.current;
      pendingRef.current = null;
      void persistReorder(previousPending, planId, period, startAtLocal);
    }

    const pending = applyOptimisticReorder(goalId, newIndex);
    if (!pending) {
      return;
    }
    pendingRef.current = pending;
    void debouncedPersist();
  }

  useEffect(() => {
    return () => {
      void debouncedPersist.flush();
    };
  }, [debouncedPersist]);

  return { reorderGoal, debouncedReorderGoal };
}

interface GoalInsightInput {
  title: string;
}

interface CreateGoalsForPlanArgs {
  planId: string;
  goals: GoalInsightInput[];
  period: string;
  startAtLocal: string;
}

export function useCreateGoalsForPlan() {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();

  return useMutation({
    mutationFn: async ({
      planId,
      goals,
      period,
      startAtLocal,
    }: CreateGoalsForPlanArgs) => {
      const goalCreates = goals.map((g) => prepareGoalCreate(g));
      const currentPlannedGoals = await queryClient.ensureQueryData(
        plannedGoalsQueryOptions(planId, api),
      );
      const plannedGoalCreates = preparePlannedGoalCreates(
        planId,
        goalCreates.map((gc) => gc.id),
        currentPlannedGoals,
      );

      const serverGoals = await api.goals.createGoals(goalCreates);
      const newPlannedGoals = await api.plannedGoals.addGoalsToPlan(
        plannedGoalCreates.map((pgc, i) => {
          const sg = serverGoals[i];
          if (!sg) {
            throw new Error('Mismatched goal create response');
          }
          return { id: pgc.id, goalId: sg.id, planId, sortKey: pgc.sortKey };
        }),
      );

      queryClient.setQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(planId),
        [...currentPlannedGoals, ...newPlannedGoals],
      );

      for (const goal of serverGoals) {
        analytics.trackAddedGoal({
          goalId: goal.id,
          period,
          startAtLocal,
          position: -1,
          source: 'aiSuggestion',
        });
      }

      return { goals: serverGoals };
    },
  });
}

const POSTGRES_NO_DATA_FOUND = 'P0002';

export function useDeleteGoalFromPlan() {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();

  return useMutation({
    mutationFn: async (variables: {
      goalId: string;
      plannedGoals: PlannedGoal[];
      planId: string;
      period: string;
      startAtLocal: string;
    }): Promise<void> => {
      const { goalId } = variables;
      try {
        await api.plannedGoals.removeGoalFromPlan(goalId);
      } catch (err: unknown) {
        if (
          err !== null &&
          typeof err === 'object' &&
          'code' in err &&
          err.code === POSTGRES_NO_DATA_FOUND
        ) {
          return;
        }
        throw err;
      }
    },

    onMutate: async (variables) => {
      const { goalId, plannedGoals, planId: mutationPlanId } = variables;

      const previousPlannedGoals = queryClient.getQueryData<PlannedGoal[]>(
        plannedGoalKeys.byPlanId(mutationPlanId),
      );
      const previousGoal = queryClient.getQueryData<Goal>(
        goalKeys.byId(goalId),
      );

      const plannedGoalToDelete = plannedGoals.find(
        (pg) => pg.goalId === goalId,
      );

      if (!plannedGoalToDelete) {
        throw new Error('Planned goal not found');
      }

      const goalDeleteSortKey = plannedGoalToDelete.sortKey;

      const optimisticPlannedGoals = makeOptimisticPlannedGoalsOnDelete(
        previousPlannedGoals || [],
        goalDeleteSortKey,
      );
      queryClient.setQueryData(
        plannedGoalKeys.byPlanId(mutationPlanId),
        optimisticPlannedGoals,
      );
      queryClient.removeQueries({ queryKey: goalKeys.byId(goalId) });

      await queryClient.cancelQueries({
        queryKey: plannedGoalKeys.byPlanId(mutationPlanId),
      });
      await queryClient.cancelQueries({ queryKey: goalKeys.byId(goalId) });

      return { previousPlannedGoals, previousGoal, goalId, goalDeleteSortKey };
    },

    onSuccess: (_data, variables) => {
      const sortedGoals = getSortedPlannedGoals(variables.plannedGoals);
      const position = sortedGoals.findIndex(
        (pg) => pg.goalId === variables.goalId,
      );

      analytics.trackDeletedGoal({
        goalId: variables.goalId,
        period: variables.period,
        startAtLocal: variables.startAtLocal,
        position: position >= 0 ? position : sortedGoals.length,
      });
    },

    onError: (error, variables, context) => {
      logError(error, 'Goal deletion failed');

      if (context) {
        queryClient.setQueryData(
          plannedGoalKeys.byPlanId(variables.planId),
          context.previousPlannedGoals,
        );
        queryClient.setQueryData(
          goalKeys.byId(context.goalId),
          context.previousGoal,
        );
      }
    },
  });
}
